import React, { useCallback } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl-next';

import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';

import { useAnalyticsEvents } from '../common/analytics/generated/use-analytics-events';
import { AsyncIntlProvider } from '../common/utils/locale';
import { useMessageFlags } from '../controllers/use-message-flags';
import { useSentryClient } from '../services/sentry';

import { ErrorBoundaryUI } from './error-boundary-ui';
import messages from './messages';
import type { NavErrorBoundaryProps } from './types';

export function NavErrorBoundary(props: NavErrorBoundaryProps) {
	return (
		<AsyncIntlProvider>
			<FlagsProvider>
				<InnerErrorBoundary {...props} />
			</FlagsProvider>
		</AsyncIntlProvider>
	);
}

function InnerErrorBoundary({
	children,
	showFlags,
	disableSentry = false,
	location,
	onError,
	sentryConfig,
	...rest
}: NavErrorBoundaryProps) {
	const { formatMessage } = useIntl();
	const { fireEvent } = useAnalyticsEvents();
	const flags = useMessageFlags({ showFlags });

	const { sentryInitialised, sentryClient } = useSentryClient(disableSentry, sentryConfig);

	const handleError = useCallback(
		(error: Error, errorInfo?: React.ErrorInfo) => {
			// Send events to sentry
			if (sentryInitialised && sentryClient && fg('navx-1574-enable-sentry-logs')) {
				try {
					sentryClient.captureException(error, {
						extra: {
							errorInfo,
							location,
						},
					});
				} catch (error) {
					// Log failure to send to Sentry
					fireEvent('operational.logToSentry.failed', {
						reason: error instanceof Error ? error.message : 'unknown',
					});
				}
			}

			// Fire Analytics event
			fireEvent('operational.errorBoundary.unhandledErrorCaught', {
				errorMessage: error.message,
				errorStack: error.stack || 'no stack trace',
			});

			// Show failure flag if enabled
			flags?.showFailure?.({
				title: formatMessage(messages.flagTitle),
				description: formatMessage(messages.flagDescription),
			});

			// handler Callback
			onError && onError(error, { ...errorInfo });
		},
		[sentryInitialised, sentryClient, fireEvent, flags, formatMessage, onError, location],
	);

	// Todo: verify UX do not display ErrorBoundaryUI if flags are provided
	const errorComponent = flags ? null : <ErrorBoundaryUI {...rest} location={location} />;

	return (
		<ErrorBoundary onError={handleError} fallback={errorComponent}>
			{children}
		</ErrorBoundary>
	);
}
